/**
 * Stale-chunk auto-recovery.
 *
 * The frontend deploy runs `aws s3 sync --delete`, which removes the previous
 * build's hashed JS chunks from S3. A tab left open across a deploy still holds
 * the OLD `index.html` in memory, so when the user navigates to a `React.lazy`
 * route the browser tries to `import()` a hashed chunk filename that no longer
 * exists → 404 → the dynamic import rejects. Nothing handles that rejection, so
 * the user is left staring at a blank screen until they hard-refresh.
 *
 * Fix: listen for Vite's native `vite:preloadError` event (dispatched when a
 * lazily-imported chunk fails to load) plus a backstop for chunk-load failures
 * that surface as generic errors/rejections, and trigger a ONE-SHOT reload to
 * pull the fresh `index.html` (and therefore the fresh chunk filenames).
 *
 * A `sessionStorage` timestamp guards against an infinite reload loop: if the
 * fresh fetch ALSO fails (e.g. a broken deploy, offline), we reload at most once
 * per window and then let the error surface rather than reloading forever.
 */

const RELOAD_TS_KEY = 'trc:stale-chunk-reload-at'
const RELOAD_COOLDOWN_MS = 10_000

/**
 * Message fragments that identify a failed dynamic-chunk import across browsers.
 * Chromium, Firefox, and Safari each phrase this differently.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
]

function looksLikeChunkError(input: unknown): boolean {
  if (!input) return false
  if (input instanceof Error) {
    if (input.name === 'ChunkLoadError') return true
    const message = input.message?.toLowerCase() ?? ''
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
  }
  const text = String(input).toLowerCase()
  return CHUNK_ERROR_PATTERNS.some((pattern) => text.includes(pattern))
}

/**
 * Reloads once, then refuses to reload again within the cooldown window so a
 * persistently-failing fetch can't loop the tab. The timestamp is written
 * BEFORE `reload()` so the guard is armed even though the reload aborts the
 * current script. sessionStorage access is wrapped: private mode / disabled
 * storage must not throw and block the reload.
 */
function reloadOnce(): void {
  let last = 0
  try {
    const stored = window.sessionStorage.getItem(RELOAD_TS_KEY)
    last = stored ? Number(stored) : 0
  } catch {
    last = 0
  }

  if (last && Date.now() - last < RELOAD_COOLDOWN_MS) {
    // A reload already happened moments ago and we're still failing — stop here
    // and let the error surface rather than spinning forever.
    return
  }

  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()))
  } catch {
    // Storage unavailable: still reload once (best effort). Without the guard a
    // truly broken deploy could loop, but a blank screen is worse than a
    // one-off flap, and most environments have working sessionStorage.
  }

  window.location.reload()
}

/**
 * Registers the global listeners. Side-effect-only; safe to call once at
 * startup. main.tsx is browser-only, but we guard on `window` regardless so an
 * accidental non-DOM import can't throw.
 */
export function initStaleChunkReload(): void {
  if (typeof window === 'undefined') return

  // Primary: Vite dispatches this the instant a lazy chunk's preload/import
  // 404s. This is the clean signal and handles the common case on its own.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    reloadOnce()
  })

  // Backstop: some chunk failures reach us as a rejected promise (the
  // React.lazy import) rather than a preloadError.
  window.addEventListener('unhandledrejection', (event) => {
    if (looksLikeChunkError(event.reason)) reloadOnce()
  })

  // Backstop: script-tag load errors bubble as window 'error' events with no
  // message payload — match on the error object when present.
  window.addEventListener('error', (event) => {
    if (looksLikeChunkError(event.error) || looksLikeChunkError(event.message)) {
      reloadOnce()
    }
  })
}
